<script setup>
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AcessoAPI from 'dashboard/api/acesso';
import { fraseDoErro } from './fraseDoErro';
import { corpoDoPapel } from '../agents/acessoDaPessoa';

import SettingsLayout from '../SettingsLayout.vue';
import CabecalhoDosPapeis from './components/CabecalhoDosPapeis.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

// ACESSO-01 (L9a): a lista de papéis. Os quatro modelos da Powerbizz vêm primeiro e só se veem e se duplicam (o editor
// abre só para leitura); os papéis da conta se editam e se apagam. O papel que a migração marcou diz, em texto, que
// precisa ser revisto e por quê.
//
// "O que pode fazer" é uma frase, como na S8: o modelo usa a frase dele no catálogo ("Cuida da conta inteira, menos o
// que é da Powerbizz."), e o papel da conta, a do servidor, que soma o que cada módulo libera e passava de oito linhas
// no Administrador. As duas param em duas linhas; o resto está no `title` e no editor, um clique adiante.
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const agentes = useMapGetter('agents/getAgents');

const papeis = ref([]);
const carregando = ref(true);

const cabecalhos = computed(() => [
  t('PAPEIS.LISTA.PAPEL'),
  t('PAPEIS.LISTA.O_QUE_PODE_FAZER'),
  t('PAPEIS.LISTA.PESSOAS'),
  t('PAPEIS.LISTA.ACOES'),
]);

const carregar = async () => {
  carregando.value = true;
  try {
    const { data } = await AcessoAPI.papeis();
    papeis.value = data.papeis;
  } catch (erro) {
    useAlert(fraseDoErro(erro, t('PAPEIS.LISTA.ERRO_AO_CARREGAR')));
  } finally {
    carregando.value = false;
  }
};

// O modelo entra pela chave, e o papel da conta pelo id.
const idDe = papel => papel.id ?? papel.chave;

const abrir = (name, params = {}) =>
  router.push({
    name,
    params: { accountId: route.params.accountId, ...params },
  });
const editar = papel => abrir('papeis_editor', { papelId: idDe(papel) });
const oQuePodeFazer = papel =>
  papel.somente_leitura && papel.descricao
    ? papel.descricao
    : papel.o_que_pode_fazer;
// O modelo só se vê: o botão dele diz "Ver", com o olho, e não "Editar", com o lápis.
const rotuloDeAbrir = papel =>
  papel.somente_leitura
    ? t('PAPEIS.LISTA.VER', { nome: papel.nome })
    : t('PAPEIS.LISTA.EDITAR', { nome: papel.nome });

// Apagar confirma no Dialog. O papel em uso não se apaga (422 `em_uso`): o Dialog diz quantas pessoas o usam e oferece
// trocar o papel delas antes (SPEC S3).
//
// - Quem usa vem da lista de pessoas (`agents/get`), pelo `papel` de cada uma. Se a conta do servidor for outra, vale a
//   lista, e o apagar do fim diz se ainda falta alguém.
// - A troca é uma pessoa de cada vez, pela API de agentes, como no modal da pessoa (`corpoDoPapel`). A recusa no meio
//   (403 `concessao_recusada`) para tudo, com a frase do servidor e quantas já foram trocadas.
const dialogoDeApagar = useTemplateRef('dialogoDeApagar');
const paraApagar = ref(null);
const apagando = ref(false);
const erroAoApagar = ref('');
const emUso = ref(false);
// O papel novo de quem usa: `modelo:<chave>` ou `papel:<id>`.
const paraQuem = ref('');
const aTrocar = ref(0);
const trocadas = ref(0);

const quemUsa = computed(() =>
  agentes.value.filter(
    agente =>
      agente.papel?.tipo === 'papel' && agente.papel.id === paraApagar.value?.id
  )
);

const opcoesDeTroca = computed(() => {
  const outros = papeis.value.filter(papel => papel !== paraApagar.value);
  return [
    {
      label: t('PAPEIS.APAGAR.MODELOS'),
      options: outros
        .filter(papel => papel.origem === 'powerbizz')
        .map(papel => ({ value: `modelo:${papel.chave}`, label: papel.nome })),
    },
    {
      label: t('PAPEIS.APAGAR.DA_CONTA'),
      options: outros
        .filter(papel => papel.origem === 'conta')
        .map(papel => ({ value: `papel:${papel.id}`, label: papel.nome })),
    },
  ].filter(grupo => grupo.options.length);
});

const semConfirmar = computed(() =>
  emUso.value
    ? quemUsa.value.length > 0 && !paraQuem.value
    : Boolean(erroAoApagar.value)
);

const pedirParaApagar = papel => {
  paraApagar.value = papel;
  erroAoApagar.value = '';
  emUso.value = false;
  paraQuem.value = '';
  aTrocar.value = 0;
  trocadas.value = 0;
  dialogoDeApagar.value?.open();
};

const apagar = async () => {
  const papel = paraApagar.value;
  apagando.value = true;
  try {
    await AcessoAPI.apagarPapel(papel.id);
    papeis.value = papeis.value.filter(cada => cada !== papel);
    dialogoDeApagar.value?.close();
    useAlert(t('PAPEIS.APAGAR.APAGADO'));
  } catch (erro) {
    if (erro?.response?.data?.codigo === 'em_uso') {
      await store.dispatch('agents/get');
      emUso.value = true;
    } else {
      erroAoApagar.value = fraseDoErro(erro, t('PAPEIS.APAGAR.ERRO'));
    }
  } finally {
    apagando.value = false;
  }
};

const trocarEApagar = async () => {
  const papel = paraApagar.value;
  const pessoas = [...quemUsa.value];
  apagando.value = true;
  erroAoApagar.value = '';
  aTrocar.value = pessoas.length;
  trocadas.value = 0;
  try {
    for (let i = 0; i < pessoas.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await store.dispatch('agents/update', {
        id: pessoas[i].id,
        ...corpoDoPapel(paraQuem.value),
      });
      trocadas.value += 1;
    }
  } catch (erro) {
    erroAoApagar.value = fraseDoErro(erro, t('PAPEIS.APAGAR.ERRO_AO_TROCAR'));
    apagando.value = false;
    return;
  }
  try {
    await AcessoAPI.apagarPapel(papel.id);
    dialogoDeApagar.value?.close();
    useAlert(t('PAPEIS.APAGAR.APAGADO'));
    // As pessoas foram para outro papel: a contagem dele mudou.
    carregar();
  } catch (erro) {
    erroAoApagar.value = fraseDoErro(erro, t('PAPEIS.APAGAR.ERRO'));
  } finally {
    apagando.value = false;
  }
};

const confirmar = () => (emUso.value ? trocarEApagar() : apagar());

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="t('PAPEIS.CARREGANDO')"
  >
    <template #header>
      <CabecalhoDosPapeis>
        <template #acoes>
          <Button
            :label="t('PAPEIS.LISTA.NOVO')"
            icon="i-lucide-plus"
            size="sm"
            data-novo-papel
            @click="abrir('papeis_novo')"
          />
        </template>
      </CabecalhoDosPapeis>
    </template>
    <template #body>
      <BaseTable :headers="cabecalhos" :items="papeis">
        <template #header-1="{ header }">
          <span class="normal-case">{{ header }}</span>
        </template>
        <template #header-2="{ header }">
          <span class="block text-end">{{ header }}</span>
        </template>
        <template #header-3="{ header }">
          <span class="sr-only">{{ header }}</span>
        </template>
        <template #row="{ items }">
          <BaseTableRow
            v-for="papel in items"
            :key="idDe(papel)"
            :item="papel"
            :data-papel="idDe(papel)"
          >
            <BaseTableCell class="align-top">
              <div class="flex flex-col min-w-0 gap-0.5">
                <span class="text-n-slate-12">{{ papel.nome }}</span>
                <span
                  v-if="papel.somente_leitura || papel.descricao"
                  class="text-label-small text-n-slate-11"
                >
                  {{
                    papel.somente_leitura
                      ? t('PAPEIS.LISTA.MODELO')
                      : papel.descricao
                  }}
                </span>
                <p
                  v-if="papel.revisar"
                  class="flex items-start gap-1.5 mt-1 mb-0 text-label-small text-n-slate-11"
                  data-revisar
                >
                  <span
                    class="i-lucide-circle-alert size-3.5 mt-px shrink-0 text-n-amber-11"
                    aria-hidden="true"
                  />
                  <span>
                    <span class="font-semibold text-n-slate-12">
                      {{ t('PAPEIS.LISTA.REVISAR') }}
                    </span>
                    {{ papel.revisar_motivo }}
                  </span>
                </p>
              </div>
            </BaseTableCell>
            <BaseTableCell class="align-top">
              <span
                class="max-w-md text-n-slate-11 line-clamp-2"
                :title="oQuePodeFazer(papel)"
                data-o-que-pode-fazer
              >
                {{ oQuePodeFazer(papel) }}
              </span>
            </BaseTableCell>
            <BaseTableCell align="end" class="align-top tabular-nums">
              {{ papel.pessoas }}
            </BaseTableCell>
            <BaseTableCell align="end" class="align-top">
              <div class="flex justify-end gap-1">
                <Button
                  v-tooltip.top="rotuloDeAbrir(papel)"
                  :aria-label="rotuloDeAbrir(papel)"
                  :icon="
                    papel.somente_leitura ? 'i-lucide-eye' : 'i-lucide-pencil'
                  "
                  slate
                  ghost
                  sm
                  data-editar
                  @click="editar(papel)"
                />
                <Button
                  v-if="!papel.somente_leitura"
                  v-tooltip.top="t('PAPEIS.LISTA.APAGAR', { nome: papel.nome })"
                  :aria-label="t('PAPEIS.LISTA.APAGAR', { nome: papel.nome })"
                  icon="i-lucide-trash-2"
                  slate
                  ghost
                  sm
                  class="hover:enabled:text-n-ruby-11"
                  data-apagar
                  @click="pedirParaApagar(papel)"
                />
              </div>
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <Dialog
      ref="dialogoDeApagar"
      type="alert"
      :title="t('PAPEIS.APAGAR.TITULO', { nome: paraApagar?.nome || '' })"
      :description="t('PAPEIS.APAGAR.DESCRICAO')"
      :confirm-button-label="
        emUso
          ? t('PAPEIS.APAGAR.TROCAR_E_APAGAR')
          : t('PAPEIS.APAGAR.CONFIRMAR')
      "
      :is-loading="apagando"
      :disable-confirm-button="semConfirmar"
      @confirm="confirmar"
    >
      <div class="flex flex-col gap-3">
        <template v-if="emUso">
          <p class="mb-0 text-sm text-n-slate-12" data-em-uso>
            {{
              t('PAPEIS.APAGAR.EM_USO', { n: quemUsa.length }, quemUsa.length)
            }}
          </p>
          <label v-if="quemUsa.length" class="flex flex-col gap-1">
            {{ t('PAPEIS.APAGAR.TROCAR_PARA', quemUsa.length) }}
            <Select
              v-model="paraQuem"
              :groups="opcoesDeTroca"
              :placeholder="t('PAPEIS.APAGAR.ESCOLHA')"
              class="!w-full"
              data-trocar-para
            />
          </label>
        </template>
        <p
          v-if="erroAoApagar"
          role="alert"
          class="flex items-start gap-2 mb-0 text-sm text-n-slate-12"
          data-erro-ao-apagar
        >
          <span
            class="i-lucide-circle-alert size-4 mt-0.5 shrink-0 text-n-ruby-11"
            aria-hidden="true"
          />
          {{ erroAoApagar }}
        </p>
        <p
          v-if="erroAoApagar && aTrocar"
          class="mb-0 text-sm text-n-slate-11"
          data-trocadas
        >
          {{ t('PAPEIS.APAGAR.TROCADAS', { n: trocadas, total: aTrocar }) }}
        </p>
      </div>
    </Dialog>
  </SettingsLayout>
</template>
